/**
 * LámFon Source Label System
 * 
 * Two-layer model:
 *   Internal: source_category + source_name + source_detail
 *   Display:  primary label (user-facing) + secondary label (metadata)
 * 
 * Trust hierarchy (highest → lowest):
 *   elder_review > translation_backbone > dictionary > literature > community > system_generated
 */

// Canonical source categories with display config
const SOURCE_CONFIG = {
  elder_review: {
    primary: 'Wirfon-reviewed',
    secondary: 'Source: Wirfon review',
    color: 'bg-[#D4A373] text-white',
    borderColor: 'border-[#D4A373]',
    textColor: 'text-[#D4A373]',
    lightBg: 'bg-[#FDF6F0]',
    icon: 'shield',
    trustRank: 0,
  },
  translation_backbone: {
    primary: 'Core translation',
    secondary: 'Source: Index',
    color: 'bg-[#4CAF50] text-white',
    borderColor: 'border-[#4CAF50]',
    textColor: 'text-[#4CAF50]',
    lightBg: 'bg-[#E8F5E9]',
    icon: 'languages',
    trustRank: 1,
  },
  dictionary: {
    primary: 'Dictionary meaning',
    secondary: 'Source: Dictionary',
    color: 'bg-[#588157] text-white',
    borderColor: 'border-[#588157]',
    textColor: 'text-[#588157]',
    lightBg: 'bg-[#EAF1EA]',
    icon: 'book-open',
    trustRank: 2,
  },
  dictionary_example: {
    primary: 'Dictionary example',
    secondary: 'Source: Dictionary',
    color: 'bg-[#588157]/80 text-white',
    borderColor: 'border-[#588157]/60',
    textColor: 'text-[#588157]',
    lightBg: 'bg-[#EAF1EA]',
    icon: 'message-square',
    trustRank: 2,
  },
  literature: {
    primary: 'Used in literature',
    secondary: 'Source: Literature',
    color: 'bg-[#78716C] text-white',
    borderColor: 'border-[#78716C]',
    textColor: 'text-[#78716C]',
    lightBg: 'bg-[#F5F5F4]',
    icon: 'book-marked',
    trustRank: 3,
  },
  lessons: {
    primary: 'Lesson example',
    secondary: 'Source: Learning module',
    color: 'bg-[#7C3AED] text-white',
    borderColor: 'border-[#7C3AED]',
    textColor: 'text-[#7C3AED]',
    lightBg: 'bg-[#F3F0FF]',
    icon: 'graduation-cap',
    trustRank: 3,
  },
  community: {
    primary: 'Community suggestion',
    secondary: 'Source: Community',
    color: 'bg-[#0891B2] text-white',
    borderColor: 'border-[#0891B2]',
    textColor: 'text-[#0891B2]',
    lightBg: 'bg-[#ECFEFF]',
    icon: 'users',
    trustRank: 4,
  },
  scientific_reference: {
    primary: 'Scientific name',
    secondary: 'Source: Scientific names index',
    color: 'bg-[#6D28D9] text-white',
    borderColor: 'border-[#6D28D9]',
    textColor: 'text-[#6D28D9]',
    lightBg: 'bg-[#F3F0FF]',
    icon: 'sparkles',
    trustRank: 5,
  },
  place_reference: {
    primary: 'Place',
    secondary: 'Source: Places index',
    color: 'bg-[#B45309] text-white',
    borderColor: 'border-[#B45309]',
    textColor: 'text-[#B45309]',
    lightBg: 'bg-[#FFFBEB]',
    icon: 'book-marked',
    trustRank: 5,
  },
  system_generated: {
    primary: 'Practice item',
    secondary: 'Source: System-generated',
    color: 'bg-[#A8A29E] text-white',
    borderColor: 'border-[#A8A29E]',
    textColor: 'text-[#A8A29E]',
    lightBg: 'bg-[#F5F5F4]',
    icon: 'sparkles',
    trustRank: 5,
  },
};

// Map raw source values to canonical categories
const SOURCE_MAP = {
  // Translation backbone
  'index': 'translation_backbone',

  // Dictionary
  'dictionary': 'dictionary',
  'dict_example': 'dictionary_example',
  'dict_sense': 'dictionary',

  // Literature
  'bible': 'literature',
  'new_testament': 'literature',
  'new_testament_lamnso': 'literature',
  'new_testament_english': 'literature',
  'folktale': 'literature',
  'proverb': 'literature',
  'oral_text': 'literature',

  // Lessons
  'literacy_guide': 'lessons',
  'how_to_read_write_lamnso': 'lessons',
  'lesson': 'lessons',

  // Community
  'user': 'community',
  'community': 'community',

  // Elder review
  'elder_review': 'elder_review',
  'elder': 'elder_review',

  // System
  'system': 'system_generated',
  'auto': 'system_generated',

  // Scientific/Places
  'scientific_reference': 'scientific_reference',
  'scientific_names_index': 'scientific_reference',
  'place_reference': 'place_reference',
  'places_index': 'place_reference',
};

// Sub-source detail labels (shown in secondary/admin views)
const SOURCE_DETAIL_MAP = {
  'bible': 'Bible',
  'new_testament': 'New Testament',
  'new_testament_lamnso': 'New Testament (Lamnso\')',
  'new_testament_english': 'New Testament (English)',
  'folktale': 'Folktale',
  'proverb': 'Proverb',
  'dictionary': 'Dictionary',
  'index': 'Index',
  'literacy_guide': 'Literacy Guide',
  'how_to_read_write_lamnso': 'How to Read & Write Lamnso\'',
  'user': 'User submission',
  'elder_review': 'Wirfon review',
};

/**
 * Resolve source info from a raw source string.
 * Returns { category, primary, secondary, detail, config }
 */
export function resolveSource(rawSource) {
  const source = (rawSource || '').toLowerCase().trim();
  const category = SOURCE_MAP[source] || 'system_generated';
  const config = SOURCE_CONFIG[category] || SOURCE_CONFIG.system_generated;
  const detail = SOURCE_DETAIL_MAP[source] || rawSource || '';

  return {
    category,
    primary: config.primary,
    secondary: detail ? `Source: ${detail}` : config.secondary,
    detail,
    ...config,
  };
}

/**
 * Compare two source labels by trust rank (for sorting).
 */
export function compareByTrust(sourceA, sourceB) {
  const a = resolveSource(sourceA);
  const b = resolveSource(sourceB);
  return a.trustRank - b.trustRank;
}

export { SOURCE_CONFIG, SOURCE_MAP };
